/**
 * loop-vars.ts — 循环变量替换工具。
 *
 * 从 ForBlockRenderer 提取，用于预处理 markdown 块中的 $each 引用。
 */

import type { DocumentBlock } from "@magic-md/core";

/**
 * 在文本中解析循环变量引用 ($ref{$each}, $ref{$each.field}, $ref{$each_index})。
 */
export function resolveLoopVarsInText(
    text: string,
    eachVar: string,
    item: unknown,
    index: number,
): string {
    return text.replace(
        /\$ref\{(\$[^}]+)\}/g,
        (_match, refPath: string) => {
            if (refPath === `$${eachVar}_index`) return String(index);
            if (refPath === `$${eachVar}`) {
                return typeof item === "string" ? item : JSON.stringify(item);
            }
            if (refPath.startsWith(`$${eachVar}.`)) {
                const fieldPath = refPath.slice(`$${eachVar}.`.length);
                let current: unknown = item;
                for (const seg of fieldPath.split(".")) {
                    if (current == null || typeof current !== "object") return String(current ?? "");
                    current = (current as Record<string, unknown>)[seg];
                }
                return typeof current === "string" ? current : JSON.stringify(current ?? null);
            }
            return _match;
        },
    );
}

/**
 * 预处理 blocks 数组，将 markdown 块中的 $ref{$each...} 替换为实际值。
 * 非 markdown 块原样返回（InlineRef 通过 ScopeContext.loopVar 处理）。
 */
export function substituteLoopVars(
    blocks: DocumentBlock[],
    eachVar: string,
    item: unknown,
    index: number,
): DocumentBlock[] {
    return blocks.map(block => {
        if (block.type === "markdown") {
            return {
                ...block,
                content: resolveLoopVarsInText(block.content, eachVar, item, index),
            };
        }
        return block;
    });
}

/**
 * 在 scope 链中查找最近的 loopVar，解析循环变量引用。
 *
 * 支持: $ref{$each}, $ref{$each_index}, $ref{$each.field.path}
 */
export function resolveLoopVarRef(
    refPath: string,
    loopVar: { name: string; item: unknown; index: number },
): unknown | undefined {
    const { name, item, index } = loopVar;
    if (refPath === `$${name}_index`) return index;
    if (refPath === `$${name}`) return item;
    if (refPath.startsWith(`$${name}.`)) {
        const fieldPath = refPath.slice(`$${name}.`.length);
        let current: unknown = item;
        for (const seg of fieldPath.split(".")) {
            if (current == null || typeof current !== "object") return current ?? undefined;
            current = (current as Record<string, unknown>)[seg];
        }
        return current;
    }
    return undefined;
}
